import {
    ConvenienceStore,
    InstallmentPlan,
    OnlineBrand,
    PaymentType,
    ResponseCharge,
    ResponseSubscription,
    ResponseTransactionToken,
    SubscriptionPeriod,
    TransactionTokenType,
} from "univapay-node";

import { SupportedLocale } from "./locale/labels";
import { CheckoutParams, CheckoutType, PatchedCardBrand, Period } from "./types";

export interface EnhancedMessageEvent extends MessageEvent {
    originalEvent: { origin: string };
}

export type Message<Data, Code = MessageType> = {
    type: Code;
    data?: Data;
    errors?: Error[];
};

export enum ResourceType {
    TRANSACTION_TOKEN = "transactionToken",
    CHARGE = "charge",
    SUBSCRIPTION = "subscription",
}

export enum MessageType {
    HANDSHAKE = "handshake",
    EXECUTE = "execute",
    OPENED = "opened",
    BEFORE_CLOSING = "before-closing",
    BEFORE_CLOSING_RESPONSE = "before-closing-response",
    CLOSED = "closed",
    SUCCESS = "success",
    PENDING = "pending",
    ERROR = "error",
    RESIZE = "resize",
    SUBMIT_CARD_DATA = "submit-card-data",
    UPDATE_PARAMETERS = "update-parameters",
    SUBMITTED = "submitted",
    TOKEN_CREATED = "token-created",
    CHARGE_CREATED = "charge-created",
    SUBSCRIPTION_CREATED = "subscription-created",
    VALIDATION_ERROR = "validation-error",
    THREE_DS_AUTHORIZATION = "three-ds-authorization",
    THREE_DS_AUTHORIZATION_SUCCESS = "three-ds-authorization-success",
    THREE_DS_AUTHORIZATION_FAILURE = "three-ds-authorization-failure",
    THREE_DS_AUTHORIZATION_MODAL = "three-ds-authorization-modal",
}

export type ConnectorMessage =
    | Message<MessageHandshake, MessageType.HANDSHAKE>
    | Message<MessageOpened, MessageType.OPENED>
    | Message<MessageExecute, MessageType.EXECUTE>
    | Message<MessageResize, MessageType.RESIZE>
    | Message<MessageSubmitted, MessageType.SUBMITTED>
    | Message<MessageSuccess, MessageType.SUCCESS>
    | Message<Record<string, unknown>, MessageType.PENDING>
    | Message<MessageError, MessageType.ERROR>
    | Message<MessageClose, MessageType.CLOSED>
    | Message<MessageUpdateParameters, MessageType.UPDATE_PARAMETERS>
    | Message<MessageBeforeClosing, MessageType.BEFORE_CLOSING>
    | Message<MessageBeforeClosingResponse, MessageType.BEFORE_CLOSING_RESPONSE>
    | Message<MessageTokenCreated, MessageType.TOKEN_CREATED>
    | Message<MessageChargeCreated, MessageType.CHARGE_CREATED>
    | Message<MessageSubscriptionCreated, MessageType.SUBSCRIPTION_CREATED>
    | Message<MessageValidationError, MessageType.VALIDATION_ERROR>
    | Message<MessageSuccess, MessageType.THREE_DS_AUTHORIZATION>
    | Message<MessageSuccess, MessageType.THREE_DS_AUTHORIZATION_SUCCESS>
    | Message<MessageError, MessageType.THREE_DS_AUTHORIZATION_FAILURE>
    | Message<MessageThreeDsAuthorizationModal, MessageType.THREE_DS_AUTHORIZATION_MODAL>;

export type CheckoutTokenData = {
    checkout: CheckoutType;
    tokenType: TransactionTokenType;
    subscriptionPeriod?: SubscriptionPeriod;
};

export type CheckoutUpdateParameters = CheckoutTokenData & {
    // Subscription data
    subscriptionInitialAmount?: number;
    subscriptionStart?: string;
    subscriptionStartIn?: Period;
    subscriptionStartDayOfMonth?: number;
    subscriptionPlan?: InstallmentPlan;
    subscriptionQty?: number;
    installmentPlan?: InstallmentPlan;
    installmentQty?: number;
    subscriptionPreserveEndOfMonth?: boolean;
    subscriptionRetryInterval?: string;

    // Payment data
    amount?: number;
    currency?: string;
    capture?: boolean;
    captureAt?: string;
    captureIn?: Period;
    captureDayOfMonth?: number;

    // UX data
    showCvv?: boolean;
    hideRecurringCheckbox?: boolean;
    hidePrivacyLink?: boolean;
    paymentMethods?: (PaymentType | OnlineBrand | PatchedCardBrand | ConvenienceStore)[];
    allowCardInstallments?: boolean;
    cardInstallmentOptions?: string[];

    // Customer data
    metadata?: Record<string, string | number | boolean | null>;
    name?: string;
    nameKana?: string;
    email?: string;
    phoneNumber?: {
        countryCode?: string;
        localNumber?: string;
    };
    address?: {
        line1?: string;
        line2?: string;
        city?: string;
        state?: string;
        zip?: string;
        countryCode?: string;
    };
};

export type CheckoutUpdateOptions = {
    /**
     * Boolean when the value should be updated even when changed by the user
     *
     * Defaults to false
     */
    forceUpdate?: boolean;
};

export interface BaseMessageType {
    connectorId: string;
}

export type MessageHandshake = BaseMessageType & {
    paired: boolean;
    pendingExec?: boolean;
};

export type MessageOpened = BaseMessageType;

export type MessageExecute = BaseMessageType & {
    checkout: string;
    appId: string;
    params: CheckoutParams;
};

export type MessageResize = BaseMessageType & {
    height: string;
};

export type MessageThreeDsAuthorizationModal = BaseMessageType & {
    chargeId?: string;
    tokenId?: string;
    storeId: string;
    endpoint: string;
    jwt: string;
    locale: SupportedLocale;
    origin: string;
};

export type MessageSubmitted = BaseMessageType & {
    token: string;
};

export type MessageUpdateParameters = BaseMessageType & {
    overrides: CheckoutUpdateParameters;
    options?: CheckoutUpdateOptions;
};

export type MessageSuccess = BaseMessageType & {
    resourceType: ResourceType;
    response: ResourceResponse;
    tokenId: string;
    chargeId?: string;
    subscriptionId?: string;
};

export type MessageError = BaseMessageType & {
    error: unknown;
    resourceType?: ResourceType;
    response?: ResourceResponse;
    tokenId: string;
    chargeId?: string;
    subscriptionId?: string;
};

export type MessageClose = BaseMessageType & { failedSubscriptionId?: string };

export type MessageBeforeClosingResponse = BaseMessageType & { shouldClose: boolean };
export type MessageBeforeClosing = BaseMessageType;

export type MessageTokenCreated = BaseMessageType;

export type MessageChargeCreated = BaseMessageType;

export type MessageSubscriptionCreated = BaseMessageType;

export type MessageExecutePayment = MessageExecute;

export type MessageValidationError = BaseMessageType;

export type MessageThreeDsAuthorization = MessageSuccess;
export type MessageThreeDsAuthorizationSuccess = MessageSuccess;
export type MessageThreeDsAuthorizationFailure = MessageError;

export type ResourceResponse = ResponseTransactionToken | ResponseCharge | ResponseSubscription;

export const isChargeResponse = (response: ResourceResponse): response is ResponseCharge =>
    (response as ResponseCharge).requestedAmount !== undefined;
